import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Select,
  SimpleGrid,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { DeleteIcon, EditIcon } from "@chakra-ui/icons";
import { LocalDate } from "@js-joda/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { auth } from "../../../core/auth";
import SingleDatePicker from "../../../shared/components/DatePicker/SingleDatePicker";
import useApi from "../../../shared/hooks/useApi";
import { useCallbackRef } from "../../../shared/hooks/useCallbackRef";
import type { PatientContractId, PatientId } from "../../../shared/schema/schema";
import ConfirmDialog from "../ConfirmDialog";
import { formatAmericanDate } from "../question-types/answerFormat";
import { type AnswerWrite, SaveAnswersError } from "../question-types/answerWrites";
import { buildIcdRows, ICD_HEADINGS } from "../question-types/blockFormat";
import { EditorShell } from "../question-types/EditorShell";
import { MutedNote } from "../question-types/printStyles";
import { PrintTable } from "../question-types/TableQuestion";
import { errorText } from "../useReviewActions";
import {
  type ContractChoice,
  contractChoices,
  defaultContractId,
  DIAGNOSIS_TYPE_OPTIONS,
  diagnosisTypeLabel,
  type DiagnosisTypeChoice,
  formatContractLabel,
  hasFormErrors,
  initialContractDiagnosisCodeForm,
  type PatientContractDiagnosisCode,
  sortCodesLikePdf,
  toCreateBody,
  toEditBody,
  validateContractDiagnosisCodeForm,
} from "./contractDiagnosisCodeForm";
import { buildIcdChartRows, ICD_CHART_HEADINGS } from "./entityPrint";
import { createSingleFlight, runEntityWrite } from "./entityWriteFlow";
import { IcdCodeAutocomplete } from "./IcdCodeAutocomplete";
import { icdCodesToPrint, type IcdSnapshot, icdSnapshotWrite } from "./icdSnapshot";

// The patient's contract diagnosis (ICD) codes, which the icdCodes / finalIcdCodes questions print.
// The editor writes them through the same endpoints and with the same fields as the classic
// patientContractDiagnosisCodeSetupModal, then refreshes the document's answer snapshot (see
// icdSnapshot.ts: the PDF prints that snapshot when the document stores one).

/**
 * Print-style table with the PDF's columns, showing what the PDF prints for the document: the
 * stored `snapshot` when there is one, else the live codes of the task's contract. `title` adds
 * the PDF's title bar. `variant` "chart": the columns a GENERAL document's icdCodes prints with
 * (it becomes a chart, see buildIcdChartRows); "table" (default): the PDF's ICD table.
 */
export function PatientContractDiagnosisCodesView(props: {
  /** The review's layout ("list" = a template document): how the table prints its dates. */
  layout?: "list" | "preview";
  patientContractId: PatientContractId;
  patientId: PatientId;
  snapshot: IcdSnapshot;
  title?: string;
  variant?: "chart" | "table";
}) {
  const { queries } = useApi();
  const codes = useQuery({
    ...queries.documentReview.patientContractDiagnosisCodes(props.patientId),
    enabled: props.snapshot === null,
  });

  const isChart = props.variant === "chart";
  const printTable = (printed: unknown[]) => (
    <PrintTable
      headings={isChart ? ICD_CHART_HEADINGS : ICD_HEADINGS}
      rows={isChart ? buildIcdChartRows(printed) : buildIcdRows(printed, props.layout)}
      title={props.title}
    />
  );

  if (props.snapshot !== null) {
    return printTable(icdCodesToPrint(props.snapshot, [], props.patientContractId));
  }

  switch (codes.status) {
    case "pending":
      return <MutedNote>Loading diagnosis codes...</MutedNote>;
    case "error":
      return <MutedNote>Could not load the patient&apos;s diagnosis codes.</MutedNote>;
    case "success":
      return printTable(icdCodesToPrint(null, codes.data, props.patientContractId));
  }
}

type FormTarget = { mode: "add" } | { code: PatientContractDiagnosisCode; mode: "edit" };

/**
 * The patient's codes, with add / edit / delete (new codes start on the task's contract). After
 * each successful write: the list is reloaded, the question's snapshot is rewritten through
 * `onSnapshotWrite` (when given with `snapshotQuestion`) to the task contract's codes, then
 * `onChanged` fires.
 */
export function PatientContractDiagnosisCodesEditor(props: {
  patientContractId: PatientContractId;
  patientId: PatientId;
  /** The question whose answer snapshot the PDF prints, and the row it is stored at. */
  snapshotQuestion?: { chartRow: number; id: number };
  onChanged: () => void;
  /** Whether the add / edit form or the delete dialog is open (closing would lose it). */
  onFormOpenChange?: (isOpen: boolean) => void;
  /** Whether a write (or its snapshot refresh) is in flight. */
  onSavingChange?: (isSaving: boolean) => void;
  onSnapshotWrite?: (write: AnswerWrite) => Promise<void>;
}) {
  const { mutations, queries } = useApi();
  const queryClient = useQueryClient();
  const toast = useToast({ position: "top" });
  const canEdit = auth.isAllowedTo("edit_patient_page_diagnosis_codes");
  const codesQuery = queries.documentReview.patientContractDiagnosisCodes(props.patientId);
  const codes = useQuery(codesQuery);
  const patient = useQuery(queries.patient.get(props.patientId));
  const [formTarget, setFormTarget] = React.useState<FormTarget | null>(null);
  const [deleting, setDeleting] = React.useState<PatientContractDiagnosisCode | null>(null);
  // The entity write went through but the snapshot write (or the reload before it) failed
  const [snapshotError, setSnapshotError] = React.useState<unknown>(null);
  // An entity write and its snapshot refresh, or a snapshot Retry, is in flight. The ref guards
  // clicks that land before the re-render; the state drives the disabled / loading UI.
  const flight = React.useRef(createSingleFlight()).current;
  const [isBusy, setIsBusy] = React.useState(false);

  const deleteMutation = useMutation(mutations.documentReview.deleteContractDiagnosisCode());

  const reportFormOpen = useCallbackRef(props.onFormOpenChange);
  const isFormOpen = formTarget !== null || deleting !== null;
  React.useEffect(() => {
    reportFormOpen(isFormOpen);
  }, [isFormOpen, reportFormOpen]);

  const reportSaving = useCallbackRef(props.onSavingChange);
  React.useEffect(() => {
    reportSaving(isBusy);
  }, [isBusy, reportSaving]);

  const today = LocalDate.now();
  const contracts = patient.data?.contracts ?? [];
  const contractLabel = (contractId: PatientContractId) => {
    const contract = contracts.find((candidate) => candidate.id === contractId);
    return contract === undefined ? `Contract ${contractId}` : formatContractLabel(contract, today);
  };

  const runGuarded = <T,>(fn: () => Promise<T>) =>
    flight.run(async () => {
      setIsBusy(true);
      try {
        return await fn();
      } finally {
        setIsBusy(false);
      }
    });

  /**
   * Reloads the codes and rewrites the snapshot from them. Resolves false on failure, which stays
   * shown with a Retry.
   */
  const refreshSnapshot = async (): Promise<boolean> => {
    try {
      const { patientContractDiagnosisCodes } = await queryClient.fetchQuery({
        ...codesQuery,
        staleTime: 0,
      });
      if (props.onSnapshotWrite !== undefined && props.snapshotQuestion !== undefined) {
        await props.onSnapshotWrite(
          icdSnapshotWrite(
            props.snapshotQuestion,
            patientContractDiagnosisCodes,
            props.patientContractId
          )
        );
      }
      setSnapshotError(null);
      return true;
    } catch (error) {
      // The host's saveAnswers rejects with a SaveAnswersError wrapping the request's failure
      setSnapshotError(error instanceof SaveAnswersError ? error.failure : error);
      return false;
    }
  };

  /** Entity write + refresh as one guarded operation; resolves true once the entity changed. */
  const runWrite = async (write: () => Promise<unknown>, successMessage: string) => {
    const outcome = await runGuarded(() =>
      runEntityWrite({
        refresh: refreshSnapshot,
        write,
        onChanged: props.onChanged,
        onSucceeded: () => toast({ title: successMessage, status: "success", duration: 3000 }),
      })
    );
    return outcome === "done" || outcome === "refreshFailed";
  };

  const confirmDelete = async () => {
    if (deleting === null) {
      return;
    }
    const code = deleting;
    if (await runWrite(() => deleteMutation.mutateAsync(code.id), "Diagnosis code removed")) {
      setDeleting(null);
    }
  };

  if (codes.status === "pending") {
    return <Text fontSize="sm">Loading diagnosis codes...</Text>;
  }
  if (codes.status === "error") {
    return (
      <Alert alignItems="center" borderRadius="md" gap={2} py={2} status="error">
        <AlertIcon />
        <AlertDescription flex={1} fontSize="sm">
          {errorText(codes.error, "Could not load the patient's diagnosis codes")}
        </AlertDescription>
        <Button size="sm" variant="outline" onClick={() => void codes.refetch()}>
          Retry
        </Button>
      </Alert>
    );
  }

  const sortedCodes = sortCodesLikePdf(codes.data);

  return (
    <Stack spacing={3}>
      {snapshotError !== null && (
        <Alert alignItems="center" borderRadius="md" gap={2} py={2} status="error">
          <AlertIcon />
          <AlertDescription flex={1} fontSize="sm">
            The diagnosis codes were saved, but the document&apos;s copy was not updated:{" "}
            {errorText(snapshotError, "network error")}
          </AlertDescription>
          <Button
            isLoading={isBusy}
            size="sm"
            variant="outline"
            onClick={() => void runGuarded(refreshSnapshot)}
          >
            Retry
          </Button>
        </Alert>
      )}

      <TableContainer>
        <Table size="sm">
          <Thead>
            <Tr>
              {ICD_HEADINGS.map((heading) => (
                <Th key={heading}>{heading}</Th>
              ))}
              <Th>Contract</Th>
              <Th>For billing</Th>
              {canEdit && <Th aria-label="Actions" />}
            </Tr>
          </Thead>
          <Tbody>
            {sortedCodes.length === 0 && (
              <Tr>
                <Td color="gray.500" colSpan={canEdit ? 8 : 7}>
                  No diagnosis codes.
                </Td>
              </Tr>
            )}
            {sortedCodes.map((code) => (
              <Tr key={code.id}>
                <Td>{diagnosisTypeLabel(code.type)}</Td>
                <Td fontWeight="semibold">{code.formattedDxCode}</Td>
                <Td whiteSpace="normal">{code.shortDesc}</Td>
                <Td>{formatAmericanDate(code.startDate)}</Td>
                <Td>{formatAmericanDate(code.endDate)}</Td>
                <Td whiteSpace="normal">{contractLabel(code.patientContractId)}</Td>
                <Td>{code.isForBilling ? "Yes" : "No"}</Td>
                {canEdit && (
                  <Td>
                    <Flex gap={1} justifyContent="flex-end">
                      <IconButton
                        aria-label={`Edit ${code.formattedDxCode}`}
                        icon={<EditIcon />}
                        isDisabled={formTarget !== null || isBusy}
                        size="xs"
                        variant="ghost"
                        onClick={() => setFormTarget({ code, mode: "edit" })}
                      />
                      <IconButton
                        aria-label={`Delete ${code.formattedDxCode}`}
                        colorScheme="red"
                        icon={<DeleteIcon />}
                        isDisabled={formTarget !== null || isBusy}
                        size="xs"
                        variant="ghost"
                        onClick={() => {
                          deleteMutation.reset();
                          setDeleting(code);
                        }}
                      />
                    </Flex>
                  </Td>
                )}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      {!canEdit && (
        <Text color="gray.500" fontSize="sm">
          You don&apos;t have permission to edit the patient&apos;s diagnosis codes.
        </Text>
      )}

      {canEdit && formTarget === null && (
        <Flex>
          <Button
            isDisabled={isBusy}
            size="sm"
            variant="outline"
            onClick={() => setFormTarget({ mode: "add" })}
          >
            Add diagnosis code
          </Button>
        </Flex>
      )}

      {canEdit && formTarget !== null && (
        <DiagnosisCodeForm
          key={formTarget.mode === "edit" ? formTarget.code.id : "add"}
          code={formTarget.mode === "edit" ? formTarget.code : null}
          contracts={contracts}
          contractsError={
            patient.isError
              ? errorText(patient.error, "Could not load the patient's contracts")
              : null
          }
          existingCodes={codes.data}
          isBusy={isBusy}
          isLoadingContracts={patient.isPending}
          preferredContractId={props.patientContractId}
          onCancel={() => setFormTarget(null)}
          onWrite={async (write) => {
            if (await runWrite(write, "Diagnosis code saved")) {
              setFormTarget(null);
            }
          }}
        />
      )}

      <ConfirmDialog
        body={
          <Stack spacing={3}>
            <Text>
              Remove{" "}
              <Text as="span" fontWeight="semibold">
                {deleting?.formattedDxCode} - {deleting?.shortDesc}
              </Text>{" "}
              from the patient&apos;s diagnosis codes?
            </Text>
            {deleteMutation.isError && (
              <Alert borderRadius="md" py={2} status="error">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  {errorText(deleteMutation.error, "Could not remove the diagnosis code")}
                </AlertDescription>
              </Alert>
            )}
          </Stack>
        }
        colorScheme="red"
        confirmLabel="Delete"
        isLoading={isBusy}
        isOpen={deleting !== null}
        title="Delete diagnosis code"
        onCancel={() => setDeleting(null)}
        onConfirm={() => void confirmDelete()}
      />
    </Stack>
  );
}

function DiagnosisCodeForm(props: {
  code: PatientContractDiagnosisCode | null;
  contracts: ReadonlyArray<ContractChoice>;
  contractsError: string | null;
  existingCodes: PatientContractDiagnosisCode[];
  /** The host's write + snapshot refresh is in flight */
  isBusy: boolean;
  isLoadingContracts: boolean;
  preferredContractId: PatientContractId;
  onCancel: () => void;
  /** Runs the entity write (and the snapshot refresh after it); ignored while one is in flight. */
  onWrite: (write: () => Promise<unknown>) => Promise<void>;
}) {
  const { mutations } = useApi();
  const today = LocalDate.now();
  const keepContractIds = [props.preferredContractId];
  if (props.code !== null) {
    keepContractIds.push(props.code.patientContractId);
  }
  const choices = contractChoices(props.contracts, today, keepContractIds);
  const [form, setForm] = React.useState(() =>
    initialContractDiagnosisCodeForm(props.code, choices, props.preferredContractId)
  );
  // The contracts may arrive after the form opened: preselect the default then too
  const [hasPickedContract, setHasPickedContract] = React.useState(false);
  const patientContractId =
    form.patientContractId ??
    (!hasPickedContract && props.code === null
      ? defaultContractId(choices, props.preferredContractId)
      : null);
  const effectiveForm = { ...form, patientContractId };

  const createMutation = useMutation(mutations.documentReview.createContractDiagnosisCode());
  const updateMutation = useMutation(mutations.documentReview.updateContractDiagnosisCode());
  const activeMutation = props.code === null ? createMutation : updateMutation;

  const errors = validateContractDiagnosisCodeForm(
    effectiveForm,
    props.existingCodes,
    props.code?.id ?? null
  );

  const save = () => {
    if (props.code === null) {
      const body = toCreateBody(effectiveForm);
      if (body !== null) {
        void props.onWrite(() => createMutation.mutateAsync(body));
      }
    } else {
      const body = toEditBody(effectiveForm);
      const id = props.code.id;
      if (body !== null) {
        void props.onWrite(() => updateMutation.mutateAsync({ body, id }));
      }
    }
  };

  const error = activeMutation.isError
    ? errorText(
        activeMutation.error,
        props.code === null
          ? "Could not add the diagnosis code"
          : "Could not save the diagnosis code"
      )
    : props.contractsError;

  return (
    <EditorShell
      canSave={!hasFormErrors(errors) && props.contractsError === null}
      error={error}
      isSaving={activeMutation.isPending || props.isBusy}
      label={props.code === null ? "Add diagnosis code" : "Edit diagnosis code"}
      onCancel={props.onCancel}
      onSave={save}
    >
      <Stack spacing={3}>
        <FormControl isRequired>
          <FormLabel fontSize="sm">ICD code</FormLabel>
          <IcdCodeAutocomplete
            value={form.icdCode}
            onChange={(icdCode) => setForm((prev) => ({ ...prev, icdCode }))}
          />
        </FormControl>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <FormControl isRequired>
            <FormLabel fontSize="sm">Patient related contract</FormLabel>
            <Select
              placeholder={props.isLoadingContracts ? "Loading contracts..." : "Select contract"}
              size="sm"
              value={patientContractId ?? ""}
              onChange={(event) => {
                setHasPickedContract(true);
                const id = choices.find((choice) => String(choice.id) === event.target.value)?.id;
                setForm((prev) => ({ ...prev, patientContractId: id ?? null }));
              }}
            >
              {choices.map((contract) => (
                <option key={contract.id} value={contract.id}>
                  {formatContractLabel(contract, today)}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl isInvalid={errors.type !== undefined}>
            <FormLabel fontSize="sm">Type</FormLabel>
            <Select
              size="sm"
              value={form.type}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, type: event.target.value as DiagnosisTypeChoice }))
              }
            >
              {DIAGNOSIS_TYPE_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.type}</FormErrorMessage>
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm">From</FormLabel>
            <SingleDatePicker
              selected={form.startDate}
              onChange={(startDate) => setForm((prev) => ({ ...prev, startDate }))}
            />
          </FormControl>

          <FormControl isInvalid={errors.endDate !== undefined}>
            <FormLabel fontSize="sm">To</FormLabel>
            <SingleDatePicker
              selected={form.endDate}
              onChange={(endDate) => setForm((prev) => ({ ...prev, endDate }))}
            />
            <FormErrorMessage>{errors.endDate}</FormErrorMessage>
          </FormControl>
        </SimpleGrid>

        <Checkbox
          isChecked={form.isForBilling}
          // As in the classic modal: set when adding only (the edit route does not take it)
          isDisabled={props.code !== null}
          size="sm"
          onChange={(event) => setForm((prev) => ({ ...prev, isForBilling: event.target.checked }))}
        >
          Is for billing
        </Checkbox>
      </Stack>
    </EditorShell>
  );
}
